import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Combobox,
  DatePickerWithYear,
  Input,
  Label,
  ToggleGroup,
  ToggleGroupItem,
} from '@repo/videa-components';
import { useGetDateFNSLocale } from '@/locales/helpers';

import type { ZuubManualCheckRequest } from '@/@types/zuub/contracts';
import type { DuplicateCheckValues, FormFillValues } from './useRunVerification';
import { usePracticeProviders } from '@/react-query/rcm/usePracticeProviders';
import { useZuubRcmOnboardingPayers } from '@/react-query/rcm/useZuubRcmOnboarding';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';
import { DependentInfoSection } from './DependentInfoSection';

const DEFAULT_DOB = new Date(1993, 6, 31); // July 31, 1993

function formatDateOfBirth(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface VerificationInformationFormProps {
  formFillValues?: FormFillValues | null;
  checkDuplicate?: (values: DuplicateCheckValues) => boolean;
  onSubmit?: (request: ZuubManualCheckRequest) => Promise<void>;
  isSubmitting?: boolean;
}

export const VerificationInformationForm = ({
  formFillValues = null,
  checkDuplicate,
  onSubmit,
  isSubmitting = false,
}: VerificationInformationFormProps) => {
  const { t } = useLingui();
  const dateLocale = useGetDateFNSLocale();
  const { trackEvent } = useTrackEvent();
  const { data: payers = [] } = useZuubRcmOnboardingPayers();
  const { data: providers = [] } = usePracticeProviders();

  const dentists = useMemo(
    () =>
      providers.map((p) => ({
        id: p.id,
        name: `${p.firstName} ${p.lastName}`.trim(),
        firstName: p.firstName,
        lastName: p.lastName,
        nationalProviderId: p.nationalProviderId,
      })),
    [providers],
  );

  const dentistOptions = useMemo(
    () => dentists.map((d) => ({ value: d.name, label: d.name })),
    [dentists],
  );
  const dentistIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dentists) {
      map.set(d.name.toLowerCase(), d.id);
    }
    return map;
  }, [dentists]);

  const sortedPayers = useMemo(
    () => payers.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [payers],
  );
  // Combobox (cmdk) filters by the value prop, so we use the payer name as value
  // and map back to the ID via a lookup.
  const payerOptions = useMemo(
    () => sortedPayers.map((p) => ({ value: p.name, label: p.name })),
    [sortedPayers],
  );
  const payerIdByName = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of sortedPayers) {
      map.set(p.name.toLowerCase(), p.id);
    }
    return map;
  }, [sortedPayers]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState<Date | undefined>(DEFAULT_DOB);
  const [memberId, setMemberId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [relationship, setRelationship] = useState<'self' | 'dependent'>('self');
  const [insuranceId, setInsuranceId] = useState<string>('placeholder');
  const [dentistId, setDentistId] = useState<string>('');
  const [depFirstName, setDepFirstName] = useState('');
  const [depLastName, setDepLastName] = useState('');
  const [depDob, setDepDob] = useState<Date | undefined>(undefined);
  const [depMemberId, setDepMemberId] = useState('');
  const [depSameAsSubscriber, setDepSameAsSubscriber] = useState(false);
  const clearForm = useCallback(() => {
    setFirstName('');
    setLastName('');
    setDob(undefined);
    setMemberId('');
    setGroupId('');
    setRelationship('self');
    setInsuranceId(payers[0] ? String(payers[0].id) : 'placeholder');
    setDentistId('');
    setDepFirstName('');
    setDepLastName('');
    setDepDob(undefined);
    setDepMemberId('');
    setDepSameAsSubscriber(false);
    trackEvent(mixpanelEvents.autoVerifyRunVerificationClearForm);
  }, [payers, trackEvent]);

  useEffect(() => {
    if (!formFillValues) return;
    setFirstName(formFillValues.firstName);
    setLastName(formFillValues.lastName);
    setDob(formFillValues.dob ?? undefined);
    setMemberId(formFillValues.memberId);
    setGroupId(formFillValues.groupId);
    if (formFillValues.insuranceId) {
      setInsuranceId(formFillValues.insuranceId);
    }
    // Match dentist by provider name
    const matchedDentist = dentists.find(
      (d) =>
        d.firstName.toLowerCase() === formFillValues.providerFirstName.toLowerCase() &&
        d.lastName.toLowerCase() === formFillValues.providerLastName.toLowerCase(),
    );
    if (matchedDentist) {
      setDentistId(matchedDentist.id);
    }
    // Fill dependent fields
    if (formFillValues.dependent) {
      setRelationship('dependent');
      setDepFirstName(formFillValues.dependent.firstName);
      setDepLastName(formFillValues.dependent.lastName);
      setDepDob(formFillValues.dependent.dob ?? undefined);
      setDepMemberId(formFillValues.dependent.memberId);
      setDepSameAsSubscriber(formFillValues.dependent.sameAsSubscriber);
    } else {
      setRelationship('self');
      setDepFirstName('');
      setDepLastName('');
      setDepDob(undefined);
      setDepMemberId('');
      setDepSameAsSubscriber(false);
    }
  }, [formFillValues, dentists]);

  const [isDuplicate, setIsDuplicate] = useState(false);
  const duplicateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!checkDuplicate) {
      setIsDuplicate(false);
      return;
    }
    if (duplicateTimerRef.current) {
      clearTimeout(duplicateTimerRef.current);
    }
    duplicateTimerRef.current = setTimeout(() => {
      setIsDuplicate(
        checkDuplicate({
          firstName,
          lastName,
          dob: dob ?? null,
          memberId,
          payerId: insuranceId,
        }),
      );
    }, 300);
    return () => {
      if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current);
    };
  }, [firstName, lastName, dob, memberId, insuranceId, checkDuplicate]);

  const handleSetDob = useCallback(
    (date: Date, _source: 'CALENDAR') => setDob(date),
    [],
  );

  const isSubscriberValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    dob != null &&
    memberId.trim().length > 0 &&
    insuranceId !== 'placeholder' &&
    !Number.isNaN(parseInt(insuranceId, 10)) &&
    dentistId.length > 0;

  const isDependentValid =
    relationship === 'self' ||
    (depFirstName.trim().length > 0 &&
      depLastName.trim().length > 0 &&
      depDob != null &&
      (depSameAsSubscriber ? memberId.trim().length > 0 : depMemberId.trim().length > 0));

  const isFormValid = isSubscriberValid && isDependentValid;

  const handleVerifyBenefits = useCallback(async () => {
    trackEvent(mixpanelEvents.autoVerifyRunVerificationVerifyBenefits);
    if (!dob) return;
    const payerIdNum = parseInt(insuranceId, 10);
    if (Number.isNaN(payerIdNum) || insuranceId === 'placeholder') return;
    const dentist = dentists.find((d) => d.id === dentistId);
    if (!dentist) return;

    const trimmedGroupId = groupId.trim();
    const request: ZuubManualCheckRequest = {
      subscriberFirstName: firstName.trim(),
      subscriberLastName: lastName.trim(),
      subscriberDateOfBirth: formatDateOfBirth(dob),
      subscriberId: memberId.trim(),
      ...(trimmedGroupId ? { groupId: trimmedGroupId } : {}),
      payerId: payerIdNum,
      providerFirstName: dentist.firstName,
      providerLastName: dentist.lastName,
      providerNationalProviderId: dentist.nationalProviderId,
      dependents:
        relationship === 'dependent' && depDob
          ? [
              {
                firstName: depFirstName.trim(),
                lastName: depLastName.trim(),
                dateOfBirth: formatDateOfBirth(depDob),
                memberId: depSameAsSubscriber
                  ? memberId.trim()
                  : depMemberId.trim(),
                sameAsSubscriber: depSameAsSubscriber,
              },
            ]
          : [],
    };

    if (onSubmit) {
      await onSubmit(request);
    }
  }, [
    dentists, dob, insuranceId, dentistId, firstName, lastName, memberId, groupId,
    onSubmit, trackEvent, relationship, depFirstName, depLastName, depDob, depMemberId, depSameAsSubscriber,
  ]);

  return (
    <div className="vai-flex vai-flex-col vai-gap-6">
      <div className="vai-flex vai-items-start vai-justify-between vai-gap-4">
        <div>
          <h2 className="vai-text-lg vai-font-semibold vai-text-gray-900">
            <Trans>Verification Information</Trans>
          </h2>
          <p className="vai-mt-1 vai-text-sm vai-text-gray-500">
            <Trans>Pull benefits information for new patients.</Trans>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={clearForm}>
          <Trans>Clear Form</Trans>
        </Button>
      </div>

      <section className="vai-space-y-4">
        <h3 className="vai-text-sm vai-font-medium vai-text-gray-700">
          <Trans>Subscriber Information</Trans>
        </h3>
        <div className="vai-grid vai-gap-4 sm:vai-grid-cols-3">
          <div className="vai-space-y-2">
            <Label htmlFor="verification-first-name">
              <Trans>First Name</Trans>
              {!firstName.trim() && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <Input
              id="verification-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t`First Name`}
            />
          </div>
          <div className="vai-space-y-2">
            <Label htmlFor="verification-last-name">
              <Trans>Last Name</Trans>
              {!lastName.trim() && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <Input
              id="verification-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t`Last Name`}
            />
          </div>
          <div className="vai-space-y-2">
            <Label>
              <Trans>DOB</Trans>
              {!dob && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <DatePickerWithYear
              date={dob}
              setDate={handleSetDob}
              placeholderText={t`Pick a date`}
              locale={dateLocale}
              buttonClassName="!vai-justify-start"
            />
          </div>
        </div>
        <div className="vai-grid vai-gap-4 sm:vai-grid-cols-3">
          <div className="vai-space-y-2">
            <Label htmlFor="verification-member-id">
              <Trans>Member ID</Trans>
              {!memberId.trim() && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <Input
              id="verification-member-id"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder={t`Member ID`}
            />
          </div>
          <div className="vai-space-y-2">
            <Label htmlFor="verification-group-id">
              <Trans>Group ID (If applicable)</Trans>
            </Label>
            <Input
              id="verification-group-id"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              placeholder={t`Group ID`}
            />
          </div>
          <div className="vai-space-y-2">
            <Label>
              <Trans>Patient&apos;s relationship to subscriber</Trans>
            </Label>
            <ToggleGroup
              type="single"
              value={relationship}
              onValueChange={(v) => v && setRelationship(v as 'self' | 'dependent')}
              className="vai-flex vai-gap-0 vai-w-fit vai-rounded-lg vai-border vai-border-gray-200 !vai-justify-start"
            >
              <ToggleGroupItem
                value="self"
                aria-label={t`Self`}
                className="vai-rounded-r-none vai-px-4 vai-text-gray-900 vai-bg-white data-[state=on]:vai-bg-blue-50 data-[state=on]:vai-text-blue-700"
              >
                <Trans>Self</Trans>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="dependent"
                aria-label={t`Dependent`}
                className="vai-rounded-l-none vai-px-4 vai-text-gray-900 vai-bg-white data-[state=on]:vai-bg-blue-50 data-[state=on]:vai-text-blue-700"
              >
                <Trans>Dependent</Trans>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      </section>

      {relationship === 'dependent' && (
        <DependentInfoSection
          firstName={depFirstName}
          onFirstNameChange={setDepFirstName}
          lastName={depLastName}
          onLastNameChange={setDepLastName}
          dob={depDob}
          onDobChange={(date) => setDepDob(date)}
          memberId={depSameAsSubscriber ? memberId : depMemberId}
          onMemberIdChange={setDepMemberId}
          sameAsSubscriber={depSameAsSubscriber}
          onSameAsSubscriberChange={setDepSameAsSubscriber}
        />
      )}

      <section className="vai-space-y-4">
        <div className="vai-grid vai-gap-4 sm:vai-grid-cols-2">
          <div className="vai-space-y-2">
            <Label>
              <Trans>Insurance</Trans>
              {insuranceId === 'placeholder' && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <Combobox
              value={
                insuranceId === 'placeholder'
                  ? ''
                  : (sortedPayers.find((p) => String(p.id) === insuranceId)
                      ?.name ?? '')
              }
              onSelect={(name) => {
                const id = payerIdByName.get(name.toLowerCase());
                setInsuranceId(id != null ? String(id) : 'placeholder');
              }}
              options={payerOptions}
              placeholder={t`Select insurance`}
              searchPlaceholder={t`Search insurance...`}
            />
          </div>
          <div className="vai-space-y-2">
            <Label>
              <Trans>Dentist</Trans>
              {!dentistId && <span className="vai-text-red-500 vai-ml-0.5">*</span>}
            </Label>
            <Combobox
              value={dentists.find((d) => d.id === dentistId)?.name ?? ''}
              onSelect={(name) => {
                const id = dentistIdByName.get(name.toLowerCase());
                setDentistId(id ?? '');
              }}
              options={dentistOptions}
              placeholder={t`Select dentist`}
              searchPlaceholder={t`Search dentist...`}
            />
          </div>
        </div>
      </section>

      <div className="vai-flex vai-flex-col vai-items-end vai-gap-2">
        <Button
          onClick={handleVerifyBenefits}
          disabled={isSubmitting || !isFormValid}
        >
          <Trans>Verify Benefits</Trans>
        </Button>
        {isDuplicate && (
          <p className="vai-text-xs vai-text-muted-foreground">
            <Trans>Same as last verification</Trans>
          </p>
        )}
      </div>
    </div>
  );
};
